/**
 * Session Report PDF Generator - FIXED VERSION
 * Generates professional engineering reports in PDF format using pdfmake
 * 
 * FIXES APPLIED:
 * 1. Consistent cell margins across all table cells
 * 2. Uniform cell structure using helper functions for ALL cells
 * 3. Fixed colSpan cells to use same base styling
 * 4. Removed margin inconsistencies in inline cell definitions
 */

// Lazy-load pdfmake to handle bundler compatibility
let pdfMakeInstance = null;

async function getPdfMake() {
  if (pdfMakeInstance) return pdfMakeInstance;
  
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
  
  pdfMakeInstance = pdfMakeModule.default || pdfMakeModule;
  
  const pdfFonts = pdfFontsModule.default || pdfFontsModule;
  if (pdfFonts.pdfMake) {
    pdfMakeInstance.vfs = pdfFonts.pdfMake.vfs;
  } else if (pdfFonts.vfs) {
    pdfMakeInstance.vfs = pdfFonts.vfs;
  }
  
  return pdfMakeInstance;
}

// ============================================================================
// CONSTANTS & STYLING
// ============================================================================

const COLORS = {
  black: '#000000',
  darkGray: '#333333',
  gray: '#666666',
  lightGray: '#999999',
  headerBg: '#E8E8E8',
  failBg: '#FFE8E8',
  passBg: '#E8FFE8',
  warnBg: '#FFF8E8',
  borderColor: '#CCCCCC'
};

const FONT_SIZES = {
  title: 18,
  heading1: 14,
  heading2: 12,
  normal: 10,
  small: 9,
  tiny: 8,
  table: 8
};

// FIX: Single consistent margin for ALL table cells
const CELL_MARGIN = [4, 3, 4, 3];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatStation(feet) {
  if (feet === null || feet === undefined) return '--';
  const stations = Math.floor(feet / 100);
  const remainder = (feet % 100).toFixed(1);
  return `${stations}+${remainder.padStart(4, '0')}`;
}

function formatDistance(feet) {
  if (feet === null || feet === undefined) return '--';
  if (feet >= 5280) {
    return `${(feet / 5280).toFixed(2)} mi`;
  }
  return `${feet.toLocaleString()} ft`;
}

function formatDate(dateString) {
  if (!dateString) return '--';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch {
    return dateString;
  }
}

function formatNumber(num, decimals = 2) {
  if (num === null || num === undefined || isNaN(num)) return '--';
  return Number(num).toFixed(decimals);
}

function getStatusColor(status) {
  switch (status) {
    case 'fail': return COLORS.failBg;
    case 'pass': return COLORS.passBg;
    case 'warn': return COLORS.warnBg;
    default: return null;
  }
}

/**
 * FIX: Base cell function with CONSISTENT margins for all cells
 */
function cell(text, options = {}) {
  const cellDef = {
    text: String(text ?? '--'),
    fontSize: FONT_SIZES.table,
    alignment: options.alignment || 'left',
    bold: options.bold || false,
    color: options.color || COLORS.black,
    margin: CELL_MARGIN  // FIX: Always use consistent margin
  };
  
  if (options.fillColor) {
    cellDef.fillColor = options.fillColor;
  }
  
  if (options.colSpan) {
    cellDef.colSpan = options.colSpan;
  }
  
  return cellDef;
}

/**
 * Header cell - uses same base structure as data cells
 */
function headerCell(text, options = {}) {
  return cell(text, {
    ...options,
    bold: true,
    fillColor: COLORS.headerBg
  });
}

/**
 * Data cell with optional status coloring
 */
function dataCell(text, options = {}) {
  const fillColor = options.status ? getStatusColor(options.status) : null;
  return cell(text, {
    ...options,
    fillColor: fillColor || options.fillColor
  });
}

// ============================================================================
// SECTION GENERATORS
// ============================================================================

function generateHeaderContent(session, project, projectConfig) {
  const meta = session?.meta || {};
  
  return [
    {
      text: 'SESSION ANALYSIS REPORT',
      style: 'title',
      alignment: 'center',
      margin: [0, 0, 0, 10]
    },
    {
      text: session?.name || session?.filename || 'Untitled Session',
      style: 'subtitle',
      alignment: 'center',
      margin: [0, 0, 0, 20]
    },
    {
      columns: [
        {
          width: '50%',
          stack: [
            { text: 'Session Information', style: 'heading2', margin: [0, 0, 0, 5] },
            { text: `Operator: ${session?.operator || meta.operator || '--'}`, style: 'normal' },
            { text: `Company: ${meta.company || '--'}`, style: 'normal' },
            { text: `Run Date: ${formatDate(session?.run_date)}`, style: 'normal' },
            { text: `Project: ${project?.name || '--'}`, style: 'normal' },
          ]
        },
        {
          width: '50%',
          stack: [
            { text: 'Equipment', style: 'heading2', margin: [0, 0, 0, 5] },
            { text: `Profiler Model: ${session?.profiler_model || '--'}`, style: 'normal' },
            { text: `Serial #: ${meta.serial_num || '--'}`, style: 'normal' },
            { text: `Certification #: ${meta.certification_num || '--'}`, style: 'normal' },
            { text: `Cert. Date: ${meta.certification_date || '--'}`, style: 'normal' },
          ]
        }
      ],
      margin: [0, 0, 0, 15]
    },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: COLORS.borderColor }], margin: [0, 5, 0, 15] }
  ];
}

function generateSettingsContent(session, projectConfig) {
  const meta = session?.meta || {};
  
  return [
    { text: 'COLLECTION SETTINGS', style: 'heading1', margin: [0, 0, 0, 10] },
    {
      table: {
        headerRows: 1,
        widths: ['40%', '30%', '30%'],
        body: [
          [headerCell('Setting'), headerCell('Value', { alignment: 'right' }), headerCell('Unit')],
          [dataCell('IRI Threshold (Type A)'), dataCell(formatNumber(projectConfig?.iri_threshold_type_a, 2), { alignment: 'right' }), dataCell('in/mi')],
          [dataCell('IRI Threshold (Type B)'), dataCell(formatNumber(projectConfig?.iri_threshold_type_b, 2), { alignment: 'right' }), dataCell('in/mi')],
          [dataCell('Reduction Length'), dataCell(meta['reduction_length(ft.)'] || '528', { alignment: 'right' }), dataCell('ft')],
          [dataCell('High-Pass Filter'), dataCell(meta['high-pass_filter(ft.)'] || '--', { alignment: 'right' }), dataCell('ft')],
          [dataCell('Low-Pass Filter'), dataCell(meta['low-pass_filter(ft.)'] || '--', { alignment: 'right' }), dataCell('ft')],
          [dataCell('Sample Rate'), dataCell(meta.sample_rate || '--', { alignment: 'right' }), dataCell('')],
          [dataCell('Collection Speed'), dataCell(meta['collection_speed(mph)'] || '--', { alignment: 'right' }), dataCell('mph')],
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 15]
    }
  ];
}

function generateSummaryContent(session, segments, events, projectConfig, isReversed) {
  let startStation = session?.analysis_start_station || session?.start_station_ft || 0;
  let endStation = session?.analysis_end_station || session?.end_station_ft || 0;
  
  if (isReversed) {
    [startStation, endStation] = [endStation, startStation];
  }
  
  const totalDistance = Math.abs(endStation - startStation);
  
  const iriValues = (segments || [])
    .filter(s => s.iri_avg_in_per_mi != null)
    .map(s => s.iri_avg_in_per_mi);
  
  const avgIRI = iriValues.length > 0 
    ? iriValues.reduce((a, b) => a + b, 0) / iriValues.length 
    : null;
  const maxIRI = iriValues.length > 0 ? Math.max(...iriValues) : null;
  const minIRI = iriValues.length > 0 ? Math.min(...iriValues) : null;
  
  const allEvents = events || [];
  const lrEvents = allEvents.filter(e => e.event_type === 'LR');
  const bumpEvents = allEvents.filter(e => e.event_type === 'Bump');
  const dipEvents = allEvents.filter(e => e.event_type === 'Dip');
  
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const segmentCount = (segments || []).length;
  const exceedingSegments = (segments || []).filter(s => 
    s.iri_avg_in_per_mi && s.iri_avg_in_per_mi > threshold
  ).length;
  
  // FIX: Use consistent cell helper for all cells including colSpan cells
  const exceedingText = segmentCount > 0 
    ? `${exceedingSegments} (${((exceedingSegments / segmentCount) * 100).toFixed(1)}%)`
    : '0';
  
  return [
    { text: 'SESSION SUMMARY', style: 'heading1', margin: [0, 0, 0, 10] },
    {
      columns: [
        {
          width: '50%',
          table: {
            widths: ['50%', '50%'],
            body: [
              [headerCell('Start Station', { alignment: 'center' }), headerCell('End Station', { alignment: 'center' })],
              [
                dataCell(formatStation(startStation), { alignment: 'center', bold: true }),
                dataCell(formatStation(endStation), { alignment: 'center', bold: true })
              ]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        {
          width: '50%',
          table: {
            widths: ['60%', '40%'],
            body: [
              [headerCell('Metric'), headerCell('Value', { alignment: 'right' })],
              [dataCell('Total Distance'), dataCell(formatDistance(totalDistance), { alignment: 'right' })],
              [dataCell('Total Segments'), dataCell(segmentCount.toLocaleString(), { alignment: 'right' })],
            ]
          },
          layout: 'lightHorizontalLines'
        }
      ],
      margin: [0, 0, 0, 10]
    },
    {
      table: {
        widths: ['40%', '20%', '20%', '20%'],
        body: [
          [
            headerCell('IRI Statistics'), 
            headerCell('Average', { alignment: 'right' }), 
            headerCell('Maximum', { alignment: 'right' }), 
            headerCell('Minimum', { alignment: 'right' })
          ],
          [
            dataCell('Mean Roughness Index (MRI)'),
            dataCell(`${formatNumber(avgIRI)} in/mi`, { alignment: 'right' }),
            dataCell(`${formatNumber(maxIRI)} in/mi`, { alignment: 'right' }),
            dataCell(`${formatNumber(minIRI)} in/mi`, { alignment: 'right' })
          ],
          [
            dataCell(`Segments Exceeding ${threshold} in/mi`),
            // FIX: Use cell() helper for colSpan cell to ensure consistent margins
            cell(exceedingText, {
              colSpan: 3,
              alignment: 'center',
              fillColor: exceedingSegments > 0 ? COLORS.warnBg : COLORS.passBg
            }),
            {}, // Empty cells for colSpan
            {}
          ]
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 10]
    },
    {
      table: {
        widths: ['50%', '25%', '25%'],
        body: [
          [headerCell('Event Type'), headerCell('Count', { alignment: 'center' }), headerCell('Status', { alignment: 'center' })],
          [
            dataCell('Localized Roughness (ALR)'),
            dataCell(lrEvents.length.toString(), { alignment: 'center' }),
            dataCell(lrEvents.length > 0 ? 'ATTENTION' : 'NONE', { 
              alignment: 'center',
              fillColor: lrEvents.length > 0 ? COLORS.warnBg : COLORS.passBg
            })
          ],
          [
            dataCell('Bumps'),
            dataCell(bumpEvents.length.toString(), { alignment: 'center' }),
            dataCell(bumpEvents.length > 0 ? 'DETECTED' : 'NONE', { 
              alignment: 'center',
              fillColor: bumpEvents.length > 0 ? COLORS.warnBg : COLORS.passBg
            })
          ],
          [
            dataCell('Dips'),
            dataCell(dipEvents.length.toString(), { alignment: 'center' }),
            dataCell(dipEvents.length > 0 ? 'DETECTED' : 'NONE', { 
              alignment: 'center',
              fillColor: dipEvents.length > 0 ? COLORS.warnBg : COLORS.passBg
            })
          ]
        ]
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 15]
    }
  ];
}

function generateRollingStraitedgeContent(events, isReversed) {
  const allEvents = events || [];
  const sortFn = (a, b) => isReversed 
    ? b.from_station_ft - a.from_station_ft 
    : a.from_station_ft - b.from_station_ft;
  
  const bumps = allEvents.filter(e => e.event_type === 'Bump').sort(sortFn);
  const dips = allEvents.filter(e => e.event_type === 'Dip').sort(sortFn);
  
  const content = [
    { text: 'ROLLING STRAIGHTEDGE EVENTS', style: 'heading1', pageBreak: 'before', margin: [0, 0, 0, 10] }
  ];
  
  content.push({ text: 'Bumps', style: 'heading2', margin: [0, 10, 0, 5] });
  
  if (bumps.length === 0) {
    content.push({ text: 'No bumps detected.', style: 'normal', color: COLORS.gray, margin: [0, 0, 0, 10] });
  } else {
    const bumpBody = [
      [
        headerCell('Location'), 
        headerCell('Wheelpath', { alignment: 'center' }), 
        headerCell('Height (in)', { alignment: 'right' }), 
        headerCell('Peak Station', { alignment: 'right' })
      ]
    ];
    
    bumps.slice(0, 50).forEach(bump => {
      const location = isReversed ? bump.to_station_ft : bump.from_station_ft;
      bumpBody.push([
        dataCell(formatStation(location)),
        dataCell(bump.wheelpath || '--', { alignment: 'center' }),
        dataCell(formatNumber(bump.magnitude_in, 3), { alignment: 'right' }),
        dataCell(formatStation(bump.peak_station_ft), { alignment: 'right' })
      ]);
    });
    
    content.push({
      table: { headerRows: 1, widths: ['30%', '25%', '20%', '25%'], body: bumpBody },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 10]
    });
    
    if (bumps.length > 50) {
      content.push({ text: `... and ${bumps.length - 50} more bumps`, style: 'small', color: COLORS.gray });
    }
  }
  
  content.push({ text: 'Dips', style: 'heading2', margin: [0, 15, 0, 5] });
  
  if (dips.length === 0) {
    content.push({ text: 'No dips detected.', style: 'normal', color: COLORS.gray, margin: [0, 0, 0, 10] });
  } else {
    const dipBody = [
      [
        headerCell('Location'), 
        headerCell('Wheelpath', { alignment: 'center' }), 
        headerCell('Depth (in)', { alignment: 'right' }), 
        headerCell('Peak Station', { alignment: 'right' })
      ]
    ];
    
    dips.slice(0, 50).forEach(dip => {
      const location = isReversed ? dip.to_station_ft : dip.from_station_ft;
      dipBody.push([
        dataCell(formatStation(location)),
        dataCell(dip.wheelpath || '--', { alignment: 'center' }),
        dataCell(formatNumber(dip.magnitude_in, 3), { alignment: 'right' }),
        dataCell(formatStation(dip.peak_station_ft), { alignment: 'right' })
      ]);
    });
    
    content.push({
      table: { headerRows: 1, widths: ['30%', '25%', '20%', '25%'], body: dipBody },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 10]
    });
    
    if (dips.length > 50) {
      content.push({ text: `... and ${dips.length - 50} more dips`, style: 'small', color: COLORS.gray });
    }
  }
  
  return content;
}

function generateIRIIntervalsContent(segments, projectConfig, isReversed) {
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const sortedSegments = [...(segments || [])].sort((a, b) => 
    isReversed 
      ? b.from_station_ft - a.from_station_ft
      : a.from_station_ft - b.from_station_ft
  );
  
  const content = [
    { text: 'IRI INTERVALS', style: 'heading1', pageBreak: 'before', margin: [0, 0, 0, 5] },
    { text: `Threshold: ${threshold} in/mi`, style: 'normal', bold: true, margin: [0, 0, 0, 10] }
  ];
  
  const tableBody = [
    [
      headerCell('Start Station'),
      headerCell('End Station'),
      headerCell('Length', { alignment: 'right' }),
      headerCell('Left IRI', { alignment: 'right' }),
      headerCell('Right IRI', { alignment: 'right' }),
      headerCell('MRI', { alignment: 'right' })
    ]
  ];
  
  const displaySegments = sortedSegments.slice(0, 100);
  
  displaySegments.forEach(seg => {
    const startSta = isReversed ? seg.to_station_ft : seg.from_station_ft;
    const endSta = isReversed ? seg.from_station_ft : seg.to_station_ft;
    
    tableBody.push([
      dataCell(formatStation(startSta)),
      dataCell(formatStation(endSta)),
      dataCell(formatDistance(seg.distance_ft), { alignment: 'right' }),
      dataCell(formatNumber(seg.iri_left_in_per_mi), { alignment: 'right' }),
      dataCell(formatNumber(seg.iri_right_in_per_mi), { alignment: 'right' }),
      dataCell(formatNumber(seg.iri_avg_in_per_mi), { alignment: 'right' })
    ]);
  });
  
  content.push({
    table: {
      headerRows: 1,
      widths: ['18%', '18%', '14%', '16%', '16%', '18%'],
      body: tableBody
    },
    layout: 'lightHorizontalLines',
    margin: [0, 0, 0, 10]
  });
  
  if (sortedSegments.length > 100) {
    content.push({ 
      text: `Showing first 100 of ${sortedSegments.length} segments`, 
      style: 'small', 
      color: COLORS.gray 
    });
  }
  
  return content;
}

function generateALRContent(events, projectConfig, isReversed) {
  const threshold = projectConfig?.iri_threshold_type_a || 160;
  const lrEvents = (events || [])
    .filter(e => e.event_type === 'LR')
    .sort((a, b) => 
      isReversed
        ? b.from_station_ft - a.from_station_ft
        : a.from_station_ft - b.from_station_ft
    );
  
  const content = [
    { text: 'LOCALIZED ROUGHNESS (ALR) EVENTS', style: 'heading1', pageBreak: 'before', margin: [0, 0, 0, 5] },
    { text: `Events exceeding IRI threshold of ${threshold} in/mi`, style: 'normal', color: COLORS.gray, margin: [0, 0, 0, 10] }
  ];
  
  if (lrEvents.length === 0) {
    content.push({ text: 'No localized roughness events detected.', style: 'normal', color: COLORS.gray });
    return content;
  }
  
  const magnitudes = lrEvents.map(e => e.magnitude_in).filter(m => m != null);
  const avgMagnitude = magnitudes.length > 0 
    ? magnitudes.reduce((a, b) => a + b, 0) / magnitudes.length 
    : null;
  const maxMagnitude = magnitudes.length > 0 ? Math.max(...magnitudes) : null;
  
  content.push({
    text: [
      { text: 'Total ALR Events: ', bold: true },
      { text: `${lrEvents.length}    ` },
      { text: 'Average Magnitude: ', bold: true },
      { text: `${formatNumber(avgMagnitude)} in/mi    ` },
      { text: 'Maximum: ', bold: true },
      { text: `${formatNumber(maxMagnitude)} in/mi` }
    ],
    style: 'normal',
    margin: [0, 0, 0, 10]
  });
  
  const tableBody = [
    [
      headerCell('From Station'),
      headerCell('To Station'),
      headerCell('Wheelpath', { alignment: 'center' }),
      headerCell('IRI (in/mi)', { alignment: 'right' }),
      headerCell('Exceeds By', { alignment: 'right' })
    ]
  ];
  
  const displayEvents = lrEvents.slice(0, 75);
  
  displayEvents.forEach(evt => {
    const exceedsBy = evt.magnitude_in ? evt.magnitude_in - threshold : null;
    const fromSta = isReversed ? evt.to_station_ft : evt.from_station_ft;
    const toSta = isReversed ? evt.from_station_ft : evt.to_station_ft;
    
    tableBody.push([
      dataCell(formatStation(fromSta)),
      dataCell(formatStation(toSta)),
      dataCell(evt.wheelpath || '--', { alignment: 'center' }),
      dataCell(formatNumber(evt.magnitude_in), { alignment: 'right', bold: true }),
      dataCell(exceedsBy > 0 ? `+${formatNumber(exceedsBy)}` : '--', { 
        alignment: 'right',
        fillColor: exceedsBy > 100 ? COLORS.failBg : COLORS.warnBg
      })
    ]);
  });
  
  content.push({
    table: {
      headerRows: 1,
      widths: ['20%', '20%', '15%', '20%', '25%'],
      body: tableBody
    },
    layout: 'lightHorizontalLines',
    margin: [0, 0, 0, 10]
  });
  
  if (lrEvents.length > 75) {
    content.push({ 
      text: `Showing first 75 of ${lrEvents.length} ALR events`, 
      style: 'small', 
      color: COLORS.gray 
    });
  }
  
  return content;
}

// ============================================================================
// MAIN EXPORT FUNCTIONS
// ============================================================================

export async function generateSessionReportPDF(options) {
  const {
    session,
    project,
    projectConfig,
    segments = [],
    events = [],
    sections = {
      header: true,
      settings: true,
      summary: true,
      rollingStraitedge: true,
      iriIntervals: true,
      alr: true
    }
  } = options;
  
  const isReversed = session?.meta?.is_reverse_stationing === true;
  
  const content = [];
  
  if (sections.header) {
    content.push(...generateHeaderContent(session, project, projectConfig));
  }
  
  if (sections.settings) {
    content.push(...generateSettingsContent(session, projectConfig));
  }
  
  if (sections.summary) {
    content.push(...generateSummaryContent(session, segments, events, projectConfig, isReversed));
  }
  
  if (sections.rollingStraitedge) {
    content.push(...generateRollingStraitedgeContent(events, isReversed));
  }
  
  if (sections.iriIntervals) {
    content.push(...generateIRIIntervalsContent(segments, projectConfig, isReversed));
  }
  
  if (sections.alr) {
    content.push(...generateALRContent(events, projectConfig, isReversed));
  }
  
  const docDefinition = {
    pageSize: 'LETTER',
    pageMargins: [50, 50, 50, 60],
    
    defaultStyle: {
      fontSize: FONT_SIZES.normal
    },
    
    styles: {
      title: {
        fontSize: FONT_SIZES.title,
        bold: true,
        color: COLORS.darkGray
      },
      subtitle: {
        fontSize: FONT_SIZES.heading1,
        color: COLORS.gray
      },
      heading1: {
        fontSize: FONT_SIZES.heading1,
        bold: true,
        color: COLORS.darkGray
      },
      heading2: {
        fontSize: FONT_SIZES.heading2,
        bold: true,
        color: COLORS.darkGray
      },
      normal: {
        fontSize: FONT_SIZES.normal
      },
      small: {
        fontSize: FONT_SIZES.small
      },
      tiny: {
        fontSize: FONT_SIZES.tiny
      }
    },
    
    footer: function(currentPage, pageCount) {
      return {
        columns: [
          { 
            text: `Generated: ${new Date().toLocaleString()}`,
            alignment: 'left',
            fontSize: FONT_SIZES.tiny,
            color: COLORS.gray,
            margin: [50, 0, 0, 0]
          },
          { 
            text: `Page ${currentPage} of ${pageCount}`,
            alignment: 'right',
            fontSize: FONT_SIZES.tiny,
            color: COLORS.gray,
            margin: [0, 0, 50, 0]
          }
        ],
        margin: [0, 20, 0, 0]
      };
    },
    
    content
  };
  
  const pdfMake = await getPdfMake();
  
  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.getBlob((blob) => {
        resolve(blob);
      });
    } catch (err) {
      reject(err);
    }
  });
}

export function generatePDFReportFilename(session) {
  const sessionName = (session?.name || session?.filename || 'session')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .substring(0, 50);
  const date = new Date().toISOString().split('T')[0];
  return `${sessionName}_Report_${date}.pdf`;
}

export function downloadPDFReport(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default generateSessionReportPDF;